import React, { memo, useEffect, useMemo } from "react";
import classNames from "classnames";
import { ButtonTestIds } from "e2e-tests/testIds";
import { twMerge } from "tailwind-merge";

import { ReactComponent as StarSVG } from "@/assets/icons/new-qs-sequence-exp/star.svg";
import StickyButton from "@/common/StickyButton";
import ZingInfoBlock from "@/common/ZingInfoBlock";
import { logAnalyticsEvent, setAnalyticsUserProperties } from "@/constants/amplitude";
import { WebEventProps, WebEventTypes } from "@/constants/event";
import { sendMetrics } from "@/constants/gtag";
import { CheckboxType, QUESTIONS_IDS, QuestionTypes } from "@/constants/questionsIds";
import useCheckboxQuestion from "@/hooks/useCheckbox";
import { HandleNext } from "@/types/common";
import { Question } from "@/types/question";
import { uncheckAllIds } from "@/utils/getCheckedItems";
import { equipmentFromAnswers, EquipmentItem, getCheckEquipment } from "@/utils/getCheckEquipment";

import useAnswers from "../../utils/useAnswers";

interface CheckboxProps {
    question: Question<QuestionTypes.Checkbox>;
    handleNext: HandleNext;
    isActive: boolean;
}
const Checkbox = ({ question, handleNext }: CheckboxProps) => {
    const { checked, getIsChecked, handleCheck, handleBulkCheck, isContinueDisabled } =
        useCheckboxQuestion(question.maxChosenOptions);
    const { answers: answersValues } = useAnswers();

    const isTileSquareType = question.checkboxType === CheckboxType.tileSquare;
    const isSquareType = question.checkboxType === CheckboxType.square;

    const sendAnalyticsEvents = () => {
        logAnalyticsEvent(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: checked,
        });
        setAnalyticsUserProperties({ [question.id]: checked });
        sendMetrics(WebEventTypes.RESULT_EVENT, {
            [WebEventProps.SCREEN]: question.event,
            [WebEventProps.RESULT]: checked,
        });
    };

    const handleContinue = () => {
        if (question.id === QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT) {
            handleNext({ [question.id]: getCheckEquipment(checked) });
        } else {
            handleNext({ [question.id]: checked });
        }

        sendAnalyticsEvents();
    };

    useEffect(() => {
        if (answersValues[question.id]) {
            if (question.id === QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT) {
                const checkedItems = equipmentFromAnswers(
                    answersValues[question.id] as EquipmentItem[]
                );
                handleBulkCheck(checkedItems);
            } else {
                handleBulkCheck(answersValues[question.id] as string[]);
            }
        }
    }, [answersValues, question, handleBulkCheck]);

    const answersItems = useMemo(
        () =>
            question.answers?.map(({ id, img, svg: SVG, description, text, isPopular }) => (
                <button
                    key={id}
                    data-zing-test-id={ButtonTestIds.CHECKBOX_OPTION}
                    className={classNames(
                        "checkbox relative flex max-h-[120px] w-full items-center justify-between rounded-2xl p-4 text-white [text-align:inherit]",
                        {
                            "checkbox--checked bg-zing-cool-blue-600 female:bg-zing-warm-tangerine-400 female:text-white":
                                getIsChecked(id),
                            "bg-zing-gray-300 female:bg-zing-warm-tangerine-100 female:text-black":
                                !getIsChecked(id),
                        }
                    )}
                    onClick={() => handleCheck(id)}
                >
                    <div className="flex h-full w-full items-center justify-between gap-4">
                        {SVG && !isTileSquareType && (
                            <div className="text-zing-24-32 leading-6">
                                <SVG />
                            </div>
                        )}
                        {img && (
                            <img
                                src={img}
                                alt=""
                                className={classNames({
                                    "h-full":
                                        question.id !== QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT,
                                    "h-14 rounded-2xl":
                                        question.id === QUESTIONS_IDS.EQUIPMENT_BY_ENVIRONMENT,
                                })}
                            />
                        )}
                        <div
                            className={classNames("flex flex-1 text-zing-18-26", {
                                "flex-col gap-1": description,
                                "flex-col items-center gap-2 text-center": isTileSquareType,
                            })}
                        >
                            {isTileSquareType && SVG && (
                                <div className="text-zing-24-32 leading-6">
                                    <SVG />
                                </div>
                            )}
                            <span className="font-semibold">{text}</span>
                            {description && (
                                <div
                                    className={classNames("text-zing-12-18 text-zing-gray-100", {
                                        "female:text-white": getIsChecked(id),
                                        "female:text-zing-neutral-cool-grey-600": !getIsChecked(id),
                                    })}
                                >
                                    {description}
                                </div>
                            )}
                        </div>
                        {isPopular && (
                            <div className="flex items-center gap-1 rounded-2xl bg-zing-warm-neon-green-500 px-2 text-zing-10-14 font-semibold uppercase text-black female:bg-white">
                                <StarSVG className="female:text-zing-warm-yellow-500" width={12} />
                                <div>Popular</div>
                            </div>
                        )}
                        {!uncheckAllIds?.includes(id) && (
                            <div
                                className={twMerge(
                                    classNames(
                                        "checkbox-circle order-4 h-6 w-6 flex-shrink-0 self-center rounded-full border-2 border-zing-gray-200 bg-black female:border-zing-warm-tangerine-300 female:bg-white",
                                        {
                                            "border-none": getIsChecked(id),
                                            "rounded-lg": isTileSquareType || isSquareType,
                                            "absolute right-2 top-2": isTileSquareType,
                                        }
                                    )
                                )}
                            />
                        )}
                    </div>
                </button>
            )) ?? null,
        [question.answers, question.id, isTileSquareType, isSquareType, getIsChecked, handleCheck]
    );

    return (
        <div className="grid h-full grow flex-col content-between gap-8">
            <div className="relative grid gap-6">
                <div className="grid gap-4">
                    <div className="z-50 whitespace-pre-line text-zing-24-32 font-semibold text-white female:text-black">
                        {question.title}
                    </div>
                    {question.subtitle && (
                        <div className="whitespace-pre-line text-zing-16-24 text-white female:text-black">
                            {question.subtitle}
                        </div>
                    )}
                </div>
                {question.id === QUESTIONS_IDS.CONDITIONS_AFFECT_DAILY_ACTIVITIES && (
                    <ZingInfoBlock
                        text="Zing Coach recommends consulting your doctor before starting any exercise program."
                        className="mt-3"
                    />
                )}
                <div
                    className={classNames("relative h-full gap-2 overflow-auto", {
                        "grid grid-cols-2": isTileSquareType,
                        "flex flex-wrap": !isTileSquareType,
                    })}
                >
                    {answersItems}
                </div>
            </div>
            <StickyButton
                onClick={handleContinue}
                isDisabled={isContinueDisabled}
                className={classNames("transition-all", {
                    "pointer-events-none invisible opacity-0": isContinueDisabled,
                })}
            />
        </div>
    );
};
export default memo(Checkbox);
